import { useState, useEffect, useMemo, useRef } from "react";

export const usePermissionsState = (
  processedPermissions,
  activeSubTab,
  formsContentType,
  groupId
) => {
  const [permissions, setPermissions] = useState(null);
  const [userModifications, setUserModifications] = useState({});
  const [lastSavedState, setLastSavedState] = useState({});
  const [trackedChanges, setTrackedChanges] = useState(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [allViewStates, setAllViewStates] = useState({});

  // Track previous group ID to detect changes
  const prevGroupIdRef = useRef(groupId);

  const getCurrentViewKey = () => `${activeSubTab}_${formsContentType}`;

  // Check if there are any unsaved changes across ALL views
  const hasAnyChanges = useMemo(() => {
    if (hasChanges) return true;
    return Object.values(userModifications).some((mod) => mod.hasChanges);
  }, [hasChanges, userModifications]);

  // Get current permissions (modified or base)
  const currentPermissions = permissions || processedPermissions;

  // Check for group changes
  useEffect(() => {
    if (prevGroupIdRef.current !== groupId) {
      // Clear all state when group changes (but not on initial mount)
      if (prevGroupIdRef.current !== undefined) {
        resetState();
      }

      prevGroupIdRef.current = groupId;
    }
  }, [groupId]);

  // Update allViewStates whenever relevant states change
  useEffect(() => {
    const newAllViewStates = {};

    // Add current view state
    const currentKey = getCurrentViewKey();
    if (currentPermissions) {
      newAllViewStates[currentKey] = {
        permissions: currentPermissions,
        hasChanges: hasChanges,
      };
    }

    // Add saved modifications from other views
    Object.keys(userModifications).forEach((viewKey) => {
      if (viewKey !== currentKey) {
        newAllViewStates[viewKey] = userModifications[viewKey];
      }
    });

    // Add last saved states for views without modifications
    Object.keys(lastSavedState).forEach((viewKey) => {
      if (!newAllViewStates[viewKey]) {
        newAllViewStates[viewKey] = {
          permissions: lastSavedState[viewKey],
          hasChanges: false,
        };
      }
    });

    setAllViewStates(newAllViewStates);
  }, [currentPermissions, userModifications, lastSavedState, hasChanges]);

  // Get all view states for sync purposes
  const getAllViewStates = () => {
    return allViewStates;
  };

  // Apply sync change from another tab
  const applySyncChange = (syncChange) => {
    const {
      targetTab,
      categoryId,
      itemId,
      permissionType,
      newValue,
      options,
      targetPermissions,
      existingTrackedChanges,
      viewKey,
      rawApiData,
    } = syncChange;

    // Use the provided viewKey
    const targetViewKey =
      viewKey ||
      (() => {
        if (targetTab === "forms") {
          return "forms_general";
        } else if (targetTab === "forms_content") {
          const contentType =
            options?.contentType || formsContentType || "general";
          return `forms_content_${contentType}`;
        }
        return null;
      })();

    // Get the most recent state for the target view
    let targetState;
    if (targetViewKey === getCurrentViewKey()) {
      targetState = currentPermissions;
    } else if (userModifications[targetViewKey]?.permissions) {
      targetState = userModifications[targetViewKey].permissions;
    } else if (lastSavedState[targetViewKey]) {
      targetState = lastSavedState[targetViewKey];
    } else {
      targetState = targetPermissions;
    }

    if (!targetState) {
      return null;
    }

    // Clone the target state to avoid mutations
    const updatedState = JSON.parse(JSON.stringify(targetState));

    // Track ALL changes that will be made (including fields)
    const allTrackedChanges = [];
    const fieldsToCleanup = [];

    // Apply the change and collect all affected items
    if (targetPermissions) {
      const affectedItems = applySinglePermissionChangeAndCollect(
        updatedState,
        targetPermissions,
        categoryId,
        itemId,
        permissionType,
        newValue,
        targetTab,
        options
      );

      // Build tracked changes for all affected items
      affectedItems.forEach((affected) => {
        const changeKey = `${targetViewKey}_${affected.id}_${
          affected.permissionType || permissionType
        }`;

        // Get original value from raw API data
        const originalValue = getOriginalPermissionValueForItem(
          affected.id,
          affected.permissionType || permissionType,
          targetTab,
          options?.contentType,
          rawApiData
        );

        // Check if there's an existing tracked change for this item
        const existingChange = existingTrackedChanges.get(changeKey);

        // Determine if we should track or cleanup this change
        if (originalValue === affected.value) {
          // Value is back to original
          if (options?.fromFormsSync) {
            // This is a sync from Forms unchecking the item - cleanup
            fieldsToCleanup.push(changeKey);
          } else if (existingChange && existingChange.isFromSync) {
            // This was synced before - keep it even though it's back to original
            const changeData = {
              ...existingChange,
              currentValue: affected.value,
              timestamp: Date.now(),
              isFromSync: true,
            };
            allTrackedChanges.push({ changeKey, changeData });
          } else {
            // Not from sync, and value is back to original - mark for cleanup
            fieldsToCleanup.push(changeKey);
          }
        } else {
          // Value is different from original - track it
          const changeData = {
            itemId: affected.id,
            categoryId: affected.type === "category" ? null : categoryId,
            permissionType: affected.permissionType || permissionType,
            originalValue,
            currentValue: affected.value,
            timestamp: Date.now(),
            viewKey: targetViewKey,
            subTab: targetTab,
            contentType:
              targetTab === "forms_content"
                ? options?.contentType || formsContentType || "general"
                : undefined,
            isFromSync: true, // Mark as sync change
          };
          allTrackedChanges.push({ changeKey, changeData });
        }
      });
    }

    // Save the updated state
    if (targetViewKey === getCurrentViewKey()) {
      setPermissions(updatedState);
    } else {
      setUserModifications((prev) => ({
        ...prev,
        [targetViewKey]: {
          permissions: updatedState,
          hasChanges:
            allTrackedChanges.length > 0 || fieldsToCleanup.length === 0,
        },
      }));
    }

    // Return both tracked changes and cleanup keys
    return {
      trackedChanges: allTrackedChanges,
      cleanupKeys: fieldsToCleanup,
    };
  };

  /**
   * Apply a single permission change and collect all affected items
   */
  const applySinglePermissionChangeAndCollect = (
    targetState,
    sourceState,
    categoryId,
    itemId,
    permissionType,
    newValue,
    targetTab,
    options
  ) => {
    const affectedItems = [];

    // Find and update the item in targetState
    if (!targetState.categories && !targetState.items) return affectedItems;

    // Handle flat structure (miscellaneous)
    if (targetState.items && !targetState.categories) {
      const item = targetState.items.find((item) => item.id === itemId);
      if (item) {
        item.permissions[permissionType] = newValue;
        affectedItems.push({
          id: itemId,
          type: "item",
          value: newValue,
          permissionType,
        });
      }
      return affectedItems;
    }

    // Check if this is a sync from Forms (has fromFormsSync flag)
    const isFromFormsSync = options?.fromFormsSync === true;

    // Handle category-level changes
    if (!itemId || itemId === categoryId) {
      const targetCategory = targetState.categories.find(
        (cat) => cat.id === categoryId
      );
      const sourceCategory = isFromFormsSync
        ? null
        : sourceState?.categories?.find((cat) => cat.id === categoryId);

      if (targetCategory) {
        targetCategory.permissions[permissionType] = newValue;
        affectedItems.push({
          id: categoryId,
          type: "category",
          value: newValue,
          permissionType,
        });

        // If syncing from Forms OR no source (direct click), update all items recursively
        if (isFromFormsSync || !sourceCategory) {
          // Direct update - cascade to all children
          const updateItemsRecursively = (items) => {
            if (!items) return;

            items.forEach((item) => {
              item.permissions[permissionType] = newValue;
              affectedItems.push({
                id: item.id,
                type: item.isParentGroup ? "parentGroup" : "item",
                value: newValue,
                permissionType,
              });

              // If this is a parent group, update its forms
              if (item.isParentGroup && item.items) {
                item.items.forEach((form) => {
                  form.permissions[permissionType] = newValue;
                  affectedItems.push({
                    id: form.id,
                    type: "form",
                    value: newValue,
                    permissionType,
                  });

                  // Update all fields in the form
                  if (form.items) {
                    form.items.forEach((field) => {
                      field.permissions[permissionType] = newValue;
                      affectedItems.push({
                        id: field.id,
                        type: "field",
                        value: newValue,
                        permissionType,
                      });
                    });
                  }
                });
              }
              // If this has nested items, update them
              else if (item.items) {
                updateItemsRecursively(item.items);
              }
            });
          };

          updateItemsRecursively(targetCategory.items);
        } else {
          // Use source-based update for within-tab changes
          if (targetCategory.items && sourceCategory.items) {
            collectItemPermissions(
              targetCategory.items,
              sourceCategory.items,
              permissionType,
              affectedItems,
              categoryId
            );
          }
        }
      }
      return affectedItems;
    }

    // Handle parent group changes (for Forms Content sync from Forms)
    if (options?.isParentGroup && options?.forceFullCascade) {
      // Find the parent group in the category
      for (const category of targetState.categories || []) {
        const parentGroup = category.items?.find((item) => item.id === itemId);

        if (parentGroup && parentGroup.isParentGroup) {
          parentGroup.permissions[permissionType] = newValue;
          affectedItems.push({
            id: itemId,
            type: "parentGroup",
            value: newValue,
            permissionType,
          });

          // Update ALL forms under this parent group
          if (parentGroup.items) {
            parentGroup.items.forEach((form) => {
              form.permissions[permissionType] = newValue;
              affectedItems.push({
                id: form.id,
                type: "form",
                value: newValue,
                permissionType,
              });

              // Update ALL fields under each form
              if (form.items) {
                form.items.forEach((field) => {
                  field.permissions[permissionType] = newValue;
                  affectedItems.push({
                    id: field.id,
                    type: "field",
                    value: newValue,
                    permissionType,
                  });
                });
              }
            });
          }
          break;
        }
      }
      return affectedItems;
    }

    // For other item-level changes, use the original source-based logic
    // This preserves the normal behavior when clicking within the same tab
    for (let i = 0; i < targetState.categories.length; i++) {
      const targetCategory = targetState.categories[i];
      const sourceCategory = sourceState?.categories?.[i];

      if (!targetCategory) continue;

      const targetItem = findItemById(targetCategory.items, itemId);
      const sourceItem = sourceCategory
        ? findItemById(sourceCategory.items, itemId)
        : null;

      if (targetItem) {
        // If we have source, use it; otherwise use newValue directly
        if (sourceItem) {
          targetItem.permissions[permissionType] =
            sourceItem.permissions[permissionType];
          affectedItems.push({
            id: itemId,
            type: "item",
            value: sourceItem.permissions[permissionType],
            permissionType,
          });

          // If this item has children, update them from source
          if (targetItem.items && sourceItem.items) {
            collectItemPermissions(
              targetItem.items,
              sourceItem.items,
              permissionType,
              affectedItems,
              categoryId
            );
          }
        } else {
          // No source - direct update with newValue
          targetItem.permissions[permissionType] = newValue;
          affectedItems.push({
            id: itemId,
            type: "item",
            value: newValue,
            permissionType,
          });

          // Update all children with the same value
          if (targetItem.items) {
            const updateChildren = (items) => {
              items.forEach((child) => {
                child.permissions[permissionType] = newValue;
                affectedItems.push({
                  id: child.id,
                  type: "child",
                  value: newValue,
                  permissionType,
                });
                if (child.items) {
                  updateChildren(child.items);
                }
              });
            };
            updateChildren(targetItem.items);
          }
        }
        break;
      }
    }

    return affectedItems;
  };
  /**
   * Find an item by ID in a nested structure
   */
  const findItemById = (items, itemId) => {
    if (!items) return null;

    for (const item of items) {
      if (item.id === itemId) {
        return item;
      }

      // Check in nested items
      if (item.items) {
        const found = findItemById(item.items, itemId);
        if (found) return found;
      }

      // Check in parent groups
      if (item.isParentGroup && item.items) {
        for (const form of item.items) {
          if (form.id === itemId) return form;

          if (form.items) {
            const found = findItemById(form.items, itemId);
            if (found) return found;
          }
        }
      }
    }

    return null;
  };

  /**
   * Collect permissions from items recursively
   */
  const collectItemPermissions = (
    targetItems,
    sourceItems,
    permissionType,
    affectedItems,
    categoryId
  ) => {
    if (!targetItems || !sourceItems) return;

    targetItems.forEach((targetItem, index) => {
      const sourceItem = sourceItems[index];
      if (sourceItem && targetItem) {
        targetItem.permissions[permissionType] =
          sourceItem.permissions[permissionType];

        // Handle numeric IDs for fields
        let itemId = targetItem.id;
        if (typeof itemId === "string") {
          if (itemId.startsWith("form_")) {
            const parts = itemId.split("_");
            itemId = parseInt(parts[1]);
          } else if (!isNaN(itemId)) {
            itemId = parseInt(itemId);
          }
        }

        affectedItems.push({
          id: itemId,
          type: targetItem.items ? "parent" : "item",
          value: sourceItem.permissions[permissionType],
          permissionType,
          categoryId,
        });

        // Recursively collect nested items
        if (targetItem.items && sourceItem.items) {
          collectItemPermissions(
            targetItem.items,
            sourceItem.items,
            permissionType,
            affectedItems,
            categoryId
          );
        }
      }
    });
  };

  /**
   * Helper to get original permission value from raw API data
   */
  const getOriginalPermissionValueForItem = (
    itemId,
    permissionType,
    targetTab,
    contentType,
    rawApiData
  ) => {
    if (!rawApiData) {
      console.warn("Raw API data not available for getting original values");
      return false;
    }

    // Parse item ID if it's in string format
    let actualItemId = itemId;
    if (typeof itemId === "string") {
      if (itemId.startsWith("form_")) {
        const parts = itemId.split("_");
        actualItemId = parseInt(parts[1]);
      } else if (itemId.startsWith("parent_")) {
        actualItemId = parseInt(itemId.replace("parent_", ""));
      } else if (!isNaN(itemId)) {
        actualItemId = parseInt(itemId);
      }
    } else {
      actualItemId = parseInt(itemId);
    }

    // Find the item in the appropriate table based on tab
    let rawItem = null;

    if (targetTab === "forms_content") {
      // Check in Forms Content tables
      if (contentType === "general") {
        rawItem = rawApiData.formS_GEN_DETAILS_TABLE?.find(
          (item) => item.itemid === actualItemId
        );
      } else if (contentType === "tabs") {
        rawItem = rawApiData.formS_TABS_TABLE?.find(
          (item) => item.itemid === actualItemId
        );
      }

      // If not found in detail tables, might be a form-level item
      if (!rawItem) {
        rawItem = rawApiData.formS_TABLE?.find(
          (item) =>
            item.itemid === actualItemId ||
            item.alldetailsformid === actualItemId
        );
      }
    } else {
      // For other tabs, check in formS_TABLE
      rawItem = rawApiData.formS_TABLE?.find(
        (item) => item.itemid === actualItemId
      );

      // Also check misc table if needed
      if (!rawItem && targetTab === "miscellaneous") {
        rawItem = rawApiData.miscellaneouS_TABLE?.find(
          (item) => item.itemid === actualItemId
        );
      }
    }

    if (!rawItem) {
      console.warn(`No raw item found for ID: ${actualItemId} in ${targetTab}`);
      return false;
    }

    // Map permission type to raw data field based on context
    let value = false;

    if (targetTab === "forms_content") {
      // CRITICAL FIX: Check if this is a form-level item
      if (rawItem.alldetailsformid) {
        // This is a form item from formS_TABLE - use Forms permission mapping
        // For Forms Content 'view' maps to 'caN_SEARCH' in the Forms table
        if (permissionType === "view") {
          value = rawItem.caN_SEARCH === 1;
        } else if (permissionType === "edit") {
          value = rawItem.caN_EDIT === 1;
        } else if (permissionType === "print") {
          value = rawItem.canprint === 1;
        }
      } else {
        // This is a field item - use Forms Content permission mapping
        if (permissionType === "view") {
          value = rawItem.canview === 1 || rawItem.view === 1;
        } else if (permissionType === "edit") {
          value = rawItem.canedit === 1 || rawItem.edt === 1;
        } else if (permissionType === "print") {
          value = rawItem.canprint === 1 || rawItem.print === 1;
        }
      }
    } else if (targetTab === "forms") {
      const permissionMap = {
        search_view: rawItem.caN_SEARCH === 1 || rawItem.view === 1,
        create: rawItem.caN_CREATE === 1 || rawItem.crt === 1,
        edit: rawItem.caN_EDIT === 1 || rawItem.edt === 1,
        delete: rawItem.caN_DELETE === 1 || rawItem.dlt === 1,
        print: rawItem.canprint === 1 || rawItem.print === 1,
      };
      value = permissionMap[permissionType] || false;
    } else if (targetTab === "tools") {
      const permissionMap = {
        view_open: rawItem.caN_SEARCH === 1 || rawItem.view === 1,
        edit: rawItem.caN_EDIT === 1 || rawItem.edt === 1,
      };
      value = permissionMap[permissionType] || false;
    } else if (targetTab === "reports") {
      value =
        permissionType === "create_view" &&
        (rawItem.caN_SEARCH === 1 || rawItem.view === 1);
    } else if (targetTab === "miscellaneous") {
      value =
        permissionType === "enabled" &&
        (rawItem.caN_SEARCH === 1 || rawItem.view === 1);
    }

    return value;
  };

  // Handle view changes - restore saved state if available
  useEffect(() => {
    const currentViewKey = getCurrentViewKey();

    const savedModifications = userModifications[currentViewKey];
    const savedState = lastSavedState[currentViewKey];

    if (savedModifications) {
      setPermissions(savedModifications.permissions);
      setHasChanges(savedModifications.hasChanges);

      // Restore tracked changes for this view
      if (savedModifications.trackedChanges) {
        const mergedChanges = new Map(trackedChanges);

        // Remove old changes for this view
        Array.from(mergedChanges.keys()).forEach((key) => {
          const change = mergedChanges.get(key);
          if (change.viewKey === currentViewKey) {
            mergedChanges.delete(key);
          }
        });

        // Add saved changes for this view
        savedModifications.trackedChanges.forEach((change, key) => {
          mergedChanges.set(key, change);
        });

        setTrackedChanges(mergedChanges);
      }
    } else if (savedState) {
      setPermissions(savedState);
      setHasChanges(false);

      // Clear tracked changes for this view
      const cleanedChanges = new Map();
      trackedChanges.forEach((change, key) => {
        if (change.viewKey !== currentViewKey) {
          cleanedChanges.set(key, change);
        }
      });
      setTrackedChanges(cleanedChanges);
    } else {
      setPermissions(null);
      setHasChanges(false);

      // Clear tracked changes for this view
      const cleanedChanges = new Map();
      trackedChanges.forEach((change, key) => {
        if (change.viewKey !== currentViewKey) {
          cleanedChanges.set(key, change);
        }
      });
      setTrackedChanges(cleanedChanges);
    }
  }, [activeSubTab, formsContentType]);

  const saveCurrentViewChanges = () => {
    if (permissions && hasChanges) {
      const currentKey = getCurrentViewKey();

      // Get tracked changes for this view only
      const viewTrackedChanges = new Map();
      trackedChanges.forEach((change, key) => {
        if (change.viewKey === currentKey) {
          viewTrackedChanges.set(key, change);
        }
      });

      setUserModifications((prev) => ({
        ...prev,
        [currentKey]: {
          permissions,
          hasChanges: true,
          trackedChanges: viewTrackedChanges,
        },
      }));
    }
  };

  const updatePermissions = (
    updatedPermissions,
    newTrackedChangesSize = null
  ) => {
    setPermissions(updatedPermissions);

    const currentViewKey = getCurrentViewKey();
    const hasTrackedChanges =
      newTrackedChangesSize !== null
        ? newTrackedChangesSize > 0
        : trackedChanges.size > 0;

    setHasChanges(hasTrackedChanges);
    setUserModifications((prev) => ({
      ...prev,
      [currentViewKey]: {
        permissions: updatedPermissions,
        hasChanges: hasTrackedChanges,
      },
    }));
  };

  const resetState = () => {
    setPermissions(null);
    setUserModifications({});
    setTrackedChanges(new Map());
    setHasChanges(false);
    setLastSavedState({});
    setAllViewStates({});
  };

  const saveChanges = () => {
    const newSavedState = { ...lastSavedState };
    const currentViewKey = getCurrentViewKey();

    // Save current view state
    if (currentPermissions) {
      newSavedState[currentViewKey] = JSON.parse(
        JSON.stringify(currentPermissions)
      );
    }

    // Save modifications from other views
    Object.keys(userModifications).forEach((viewKey) => {
      if (viewKey !== currentViewKey && userModifications[viewKey].hasChanges) {
        newSavedState[viewKey] = JSON.parse(
          JSON.stringify(userModifications[viewKey].permissions)
        );
      }
    });

    setLastSavedState(newSavedState);

    // Clear changes
    setUserModifications({});
    setTrackedChanges(new Map());
    setHasChanges(false);
  };

  return {
    currentPermissions,
    hasChanges,
    hasAnyChanges,
    trackedChanges,
    setTrackedChanges,
    lastSavedState,
    getCurrentViewKey,
    getAllViewStates,
    applySyncChange,
    updatePermissions,
    saveCurrentViewChanges,
    resetState,
    saveChanges,
  };
};
